/*
 * <tc-tile-carousel> — transform-based tile carousel.
 *
 * Behaviour mirrors the reference site (moddedmotoco.com), which uses a custom
 * element rather than a library:
 *   - translate3d on a track inside an overflow:hidden wrapper (NOT native
 *     scroll), transitioned with `transform <duration>ms ease-out`
 *   - auto-advances every `data-autoplay` ms
 *   - any interaction (hover, focus, drag, arrow, dot) pauses autoplay, and it
 *     resumes `data-resume` ms after the interaction ends
 *   - drag / swipe via pointer events, snapping to the nearest slide on release
 *   - clickable dots, one per scroll position, reflecting the current index
 *
 * Accessibility: autoplay is disabled entirely under prefers-reduced-motion,
 * and paused while the tab is hidden.
 *
 * POINTER CAPTURE — READ BEFORE CHANGING THE DRAG CODE
 * ---------------------------------------------------------------------------
 * An earlier version called `track.setPointerCapture()` on every pointerdown.
 * Per the Pointer Events spec the compatibility `click` event is dispatched to
 * the CAPTURE TARGET, so capturing on the track retargeted every click from the
 * tile's <a> to the track <div>. The anchors kept their correct hrefs and still
 * looked clickable, but the browser never activated them — the whole carousel
 * silently stopped navigating.
 *
 * The pointer is therefore captured ONLY once movement passes DRAG_THRESHOLD,
 * i.e. once the gesture is unambiguously a drag and no click is coming. A plain
 * click never captures, so it reaches the anchor normally.
 *
 * The old code also never reset `moved` after a drag, so the "swallow the click
 * that follows a drag" guard stayed armed and could eat a later, genuine click.
 * Suppression now runs off a one-shot `suppressClick` flag instead.
 */
(function () {
  if (customElements.get('tc-tile-carousel')) return;

  /* Pixels of movement before a press counts as a drag rather than a click. */
  var DRAG_THRESHOLD = 6;

  customElements.define(
    'tc-tile-carousel',
    class TcTileCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-tilecar-track]');
        this.viewport = this.querySelector('[data-tilecar-viewport]');
        this.slides = Array.from(this.querySelectorAll('[data-tilecar-slide]'));
        this.dotsWrap = this.querySelector('[data-tilecar-dots]');
        this.prevBtn = this.querySelector('[data-tilecar-prev]');
        this.nextBtn = this.querySelector('[data-tilecar-next]');
        if (!this.track || this.slides.length < 2) return;

        this.index = 0;
        this.offset = 0;
        this.pointerDown = false;
        this.dragging = false;
        this.captured = false;
        this.suppressClick = false;
        this.moved = 0;
        this.autoMs = parseInt(this.dataset.autoplay, 10) || 0;
        this.resumeMs = parseInt(this.dataset.resume, 10) || 4500;
        this.durMs = parseInt(this.dataset.duration, 10) || 1000;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.setTransition(true);
        this.buildDots();
        this.bind();
        this.go(0, false);
        this.maybeStart();
      }

      disconnectedCallback() {
        this.stop();
        this.clearResume();
        this.clearSuppress();
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
      }

      /* ---------- geometry ---------- */

      step() {
        const a = this.slides[0].getBoundingClientRect();
        const b = this.slides[1] ? this.slides[1].getBoundingClientRect() : null;
        return b ? b.left - a.left : a.width;
      }

      maxIndex() {
        const step = this.step();
        if (!step) return 0;
        const visible = Math.floor((this.viewport.clientWidth + 1) / step);
        return Math.max(0, this.slides.length - Math.max(1, visible));
      }

      setTransition(on) {
        this.track.style.transition = on ? 'transform ' + this.durMs + 'ms ease-out' : 'none';
      }

      /* ---------- movement ---------- */

      go(i, animate) {
        const max = this.maxIndex();
        if (i > max) i = 0;
        if (i < 0) i = max;
        this.index = i;
        this.setTransition(animate !== false);
        this.offset = -i * this.step();
        this.track.style.transform = 'translate3d(' + this.offset + 'px,0,0)';
        this.updateDots();
      }

      next() { this.go(this.index + 1); }
      prev() { this.go(this.index - 1); }

      /* ---------- dots ---------- */

      buildDots() {
        if (!this.dotsWrap) return;
        this.dotsWrap.innerHTML = '';
        const count = this.maxIndex() + 1;
        if (count < 2) return;
        for (let i = 0; i < count; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'tc-tilecar__dot';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', () => {
            this.pause();
            this.go(i);
            this.scheduleResume();
          });
          this.dotsWrap.appendChild(b);
        }
        this.dots = Array.from(this.dotsWrap.children);
      }

      updateDots() {
        if (!this.dots) return;
        this.dots.forEach((d, i) => {
          const on = i === this.index;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      /* ---------- autoplay ---------- */

      maybeStart() {
        if (!this.autoMs || this.reduced || document.hidden) return;
        this.stop();
        this.timer = setInterval(() => this.next(), this.autoMs);
      }

      stop() {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
      }

      pause() { this.stop(); this.clearResume(); }

      clearResume() {
        if (this.resumeTimer) { clearTimeout(this.resumeTimer); this.resumeTimer = null; }
      }

      scheduleResume() {
        this.clearResume();
        if (!this.autoMs || this.reduced) return;
        this.resumeTimer = setTimeout(() => this.maybeStart(), this.resumeMs);
      }

      clearSuppress() {
        if (this.suppressTimer) { clearTimeout(this.suppressTimer); this.suppressTimer = null; }
      }

      /* ---------- events ---------- */

      bind() {
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.pause(); this.prev(); this.scheduleResume(); });
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.pause(); this.next(); this.scheduleResume(); });

        this.addEventListener('pointerenter', () => this.pause());
        this.addEventListener('pointerleave', () => { if (!this.dragging) this.scheduleResume(); });
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.scheduleResume());

        /* Drag / swipe.
           pointerdown only records the start — it must NOT capture the pointer,
           or the click that follows is retargeted away from the tile's <a>. */
        this.track.addEventListener('pointerdown', (e) => {
          if (e.button !== undefined && e.button !== 0) return;
          this.pointerDown = true;
          this.dragging = false;
          this.captured = false;
          this.suppressClick = false;
          this.clearSuppress();
          this.startX = e.clientX;
          this.startOffset = this.offset;
          this.moved = 0;
          this.pause();
        });

        this.track.addEventListener('pointermove', (e) => {
          if (!this.pointerDown) return;
          this.moved = e.clientX - this.startX;

          /* Promote to a real drag only past the threshold. Capturing here (and
             not on pointerdown) is what keeps plain clicks working. */
          if (!this.dragging && Math.abs(this.moved) > DRAG_THRESHOLD) {
            this.dragging = true;
            this.setTransition(false);
            this.classList.add('is-dragging');
            try {
              this.track.setPointerCapture(e.pointerId);
              this.captured = true;
            } catch (err) { /* capture is best-effort */ }
          }

          if (this.dragging) {
            this.track.style.transform = 'translate3d(' + (this.startOffset + this.moved) + 'px,0,0)';
          }
        });

        const end = (e) => {
          if (!this.pointerDown) return;
          this.pointerDown = false;

          if (this.captured && e && e.pointerId !== undefined) {
            try { this.track.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
          }
          this.captured = false;

          const wasDragging = this.dragging;
          this.dragging = false;
          this.classList.remove('is-dragging');

          if (wasDragging) {
            const step = this.step();
            const moved = this.moved || 0;
            let target = this.index;
            if (step && Math.abs(moved) > step * 0.15) {
              target = this.index + (moved < 0 ? 1 : -1);
            }
            this.go(Math.max(0, Math.min(this.maxIndex(), target)));
          }

          this.moved = 0;
          this.scheduleResume();

          /* Arm the one-shot guard so the click generated by this drag is
             swallowed. If no click follows (drag ended off-target), drop the
             guard shortly after so the NEXT genuine click still works. */
          this.suppressClick = wasDragging;
          this.clearSuppress();
          if (wasDragging) {
            this.suppressTimer = setTimeout(() => {
              this.suppressClick = false;
              this.suppressTimer = null;
            }, 400);
          }
        };
        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', end);

        /* Swallow only the click that a drag produced — once. */
        this.track.addEventListener('click', (e) => {
          if (this.suppressClick) {
            e.preventDefault();
            e.stopPropagation();
            this.suppressClick = false;
            this.clearSuppress();
          }
        }, true);

        this.track.addEventListener('dragstart', (e) => e.preventDefault());

        this._onResize = () => { this.buildDots(); this.go(Math.min(this.index, this.maxIndex()), false); };
        window.addEventListener('resize', this._onResize, { passive: true });

        this._onVis = () => { if (document.hidden) this.stop(); else this.maybeStart(); };
        document.addEventListener('visibilitychange', this._onVis);
      }
    }
  );
})();
